import { Checkbox, Pill, Typography } from '@recruit-robin/robin-theme/web-components';
import { Calendar, MarkerPin_01, PhoneCall_01, VideoRecorder } from '@recruit-robin/robin-theme/web-icons';

import {
  InterviewType,
  useNylasCalendarInterviewType,
  useNylasCalendarSetInterviewType,
} from '@/features/nylas-calendar-manage-connection-modal';
import { useDictionary } from '@/shared/lib/i18n';
import { GlobalDialogType } from '@/stores/global-dialogs/global-dialogs.store.types';
import { useGlobalDialog } from '@/stores/global-dialogs/hooks/useGlobalDialog';
import { useStyles } from './AutoSendInterviewInvite.styles';

const InterviewTypePill = ({
  interviewType,
  onClick,
}: {
  interviewType: InterviewType;
  onClick: React.MouseEventHandler<HTMLDivElement>;
}) => {
  const classes = useStyles();
  const dictionary = useDictionary();

  if (interviewType === InterviewType.Online) {
    return (
      <Pill
        customCss={classes.autoSendInviteInterviewType}
        onClick={onClick}
        style="icon-removable"
        type="subtle2"
        startIcon={VideoRecorder}
        label={dictionary.onlineInterview}
        size="small"
      />
    );
  }
  if (interviewType === InterviewType.InPerson) {
    return (
      <Pill
        customCss={classes.autoSendInviteInterviewType}
        onClick={onClick}
        style="icon-removable"
        type="subtle2"
        startIcon={MarkerPin_01}
        label={dictionary.inPersonInterview}
        size="small"
      />
    );
  }
  if (interviewType === InterviewType.Call) {
    return (
      <Pill
        customCss={classes.autoSendInviteInterviewType}
        onClick={onClick}
        style="icon-removable"
        type="subtle2"
        startIcon={PhoneCall_01}
        label={dictionary.callInterview}
        size="small"
      />
    );
  }

  return null;
};

export const AutoSendInterviewInvite = ({
  isAutoSendInterviewInviteEnabled,
  onToggle,
}: {
  isAutoSendInterviewInviteEnabled: boolean;
  onToggle: () => void;
}) => {
  const classes = useStyles();
  const dictionary = useDictionary();

  const interviewType = useNylasCalendarInterviewType();
  const setInterviewType = useNylasCalendarSetInterviewType();
  const nylasManagerGlobalDialog = useGlobalDialog(GlobalDialogType.NylasCalendarManageConnectionModal);

  const handleOpenNylasManagerGlobalDialog = () => {
    nylasManagerGlobalDialog.open({
      payload: {
        initialInterviewType: interviewType,
        skipManageConnection: true,
        onFinish: (interviewType) => {
          setInterviewType(interviewType);
          nylasManagerGlobalDialog.close();
        },
        onCancel: () => {
          nylasManagerGlobalDialog.close();
        },
      },
    });
  };

  return (
    <div
      css={[
        classes.autoSendInviteCard,
        isAutoSendInterviewInviteEnabled ? classes.autoSendInviteCardEnabled : classes.autoSendInviteCardDisabled,
      ]}
      onClick={onToggle}>
      <div css={classes.autoSendInviteCheckbox}>
        <Checkbox checked={isAutoSendInterviewInviteEnabled} />
      </div>
      <div css={classes.autoSendInviteContent}>
        <div css={classes.autoSendInviteHeader}>
          <Typography
            variant={(v) => v.body.long}
            color={(c) => (isAutoSendInterviewInviteEnabled ? c.neutral.variant[80] : c.neutral.variant[60])}
            inline
            ellipsis>
            {dictionary.autoSendInterviewInviteTitle}
          </Typography>

          <Calendar
            size={20}
            color={(c) => (isAutoSendInterviewInviteEnabled ? c.neutral.variant[80] : c.neutral.variant[60])}
          />

          <InterviewTypePill
            interviewType={interviewType}
            onClick={(e) => [e.stopPropagation(), handleOpenNylasManagerGlobalDialog()]}
          />
        </div>

        <Typography
          variant={(v) => v.body.short}
          color={(c) => (isAutoSendInterviewInviteEnabled ? c.neutral.variant[70] : c.neutral.variant[60])}>
          {dictionary.autoSendInterviewInvite}
        </Typography>
      </div>
    </div>
  );
};
